import type { AsortiCell } from '@app/components/asorti-grid'
import type { AsortiSizeCell, EmirStatus, StatusFilter } from './production-api'

/**
 * Formatting helpers for the production screens.
 *
 * API dates are ISO strings with no timezone ("2026-04-28T08:47:18") — the
 * source columns are `timestamp without time zone` and hold the factory's
 * local time. Converting to a `Date` would shift them by the browser's own
 * timezone, so we parse and format the string directly instead.
 */

const numberFormat = new Intl.NumberFormat('tr-TR')
const decimalFormat = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 })

export function formatNumber(value: number | null | undefined): string {
  return numberFormat.format(value ?? 0)
}

export function formatDecimal(value: number | null | undefined): string {
  return decimalFormat.format(value ?? 0)
}

/** "2026-04-28T08:47:18" → "28.04.2026". */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const [day] = value.split('T')
  const parts = day?.split('-')
  if (!parts || parts.length !== 3) return value
  const [year, month, dayNo] = parts
  return `${dayNo}.${month}.${year}`
}

/** "2026-04-28T08:47:18" → "28.04.2026 08:47". */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const [day, time] = value.split('T')
  if (!day) return value
  const shown = formatDate(day)
  return time ? `${shown} ${time.slice(0, 5)}` : shown
}

export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'info'

/**
 * Planned (`''`) gets its own tone rather than neutral: it is the
 * second-largest bucket and the one waiting on a decision, and in neutral gray
 * it read as "nothing to report" — the same tone empty badges use.
 */
export function statusTone(status: EmirStatus): Tone {
  if (status === 'X') return 'ok'
  if (status === 'B') return 'info'
  return 'warn'
}

/**
 * What each status filter actually means, one line each — shown as chip
 * tooltips and, for the planned bucket, as the list subtitle. 'Planlandı'
 * alone reads as "scheduled"; without this nothing on the screen said it
 * means "created, nothing released to the floor yet".
 */
export const STATUS_HINT: Record<StatusFilter, string> = {
  all: 'Her durumdaki üretim emirleri.',
  planned: 'Emir açıldı, henüz üretime verilmedi.',
  inProgress: 'Sahada açık işi olan emirler.',
  done: 'Kapatılmış emirler.',
}

/** Delay text: a positive day count means past due, a negative one means days remaining. */
export function delayLabel(days: number | null): string | null {
  if (days === null) return null
  if (days > 0) return `${formatNumber(days)} gün gecikti`
  if (days === 0) return 'Termin bugün'
  return `${formatNumber(Math.abs(days))} gün kaldı`
}

/**
 * The planned variant: nothing was ever released, so a past termin is a stale
 * plan, not late production — "gecikti" would raise the same alarm as a job
 * genuinely stuck on the floor, and with hundreds of abandoned plans in the
 * data that flood of red desensitizes the reader to the real fires.
 */
export function plannedDelayLabel(days: number | null): string | null {
  if (days === null) return null
  if (days > 0) return `${formatNumber(days)} gün önceydi`
  if (days === 0) return 'Termin bugün'
  return `${formatNumber(Math.abs(days))} gün kaldı`
}

export function toAsortiCells(cells: readonly AsortiSizeCell[]): AsortiCell[] {
  return cells.map((cell) => ({ size: cell.size, qty: cell.quantity }))
}
